import React, { useState } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import { debugService } from '../services/debugService';
import './Debug.css';

const Debug = ({ user }) => {
  const [kvLoading, setKvLoading] = useState(false);
  const [kvResult, setKvResult] = useState(null);
  const [kvError, setKvError] = useState(null);
  const [copyDone, setCopyDone] = useState(false);

  const [holidayYear, setHolidayYear] = useState(() => String(new Date().getFullYear()));
  const [holProvince, setHolProvince] = useState('BC');
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayResult, setHolidayResult] = useState(null);
  const [holidayError, setHolidayError] = useState(null);
  const [holidayCopyDone, setHolidayCopyDone] = useState(false);

  const [drillzLoading, setDrillzLoading] = useState(false);
  const [drillzResult, setDrillzResult] = useState(null);
  const [drillzError, setDrillzError] = useState(null);
  const [drillzCopyDone, setDrillzCopyDone] = useState(false);

  const [geomHoleId, setGeomHoleId] = useState('26SCM358');
  const [geomLoading, setGeomLoading] = useState(false);
  const [geomResult, setGeomResult] = useState(null);
  const [geomError, setGeomError] = useState(null);
  const [geomCopyDone, setGeomCopyDone] = useState(false);

  const [listValuesTableCode, setListValuesTableCode] = useState('quicklog');
  const [listValuesFieldCodes, setListValuesFieldCodes] = useState('rock_type,min_style,min_type');
  const [listValuesLoading, setListValuesLoading] = useState(false);
  const [listValuesResult, setListValuesResult] = useState(null);
  const [listValuesError, setListValuesError] = useState(null);
  const [listValuesCopyDone, setListValuesCopyDone] = useState(false);

  const [procurifyAssigneeEmail, setProcurifyAssigneeEmail] = useState(
    'riyan.abraham@selkirkcopper.com'
  );
  const [procurifyPage, setProcurifyPage] = useState('1');
  const [procurifyPageSize, setProcurifyPageSize] = useState('25');
  const [procurifyIncludeRaw, setProcurifyIncludeRaw] = useState(false);
  const [procurifyLoading, setProcurifyLoading] = useState(false);
  const [procurifyResult, setProcurifyResult] = useState(null);
  const [procurifyError, setProcurifyError] = useState(null);
  const [procurifyCopyDone, setProcurifyCopyDone] = useState(false);

  const drillzGeometryFlagOn = String(process.env.REACT_APP_3D_USE_DRILLZ_GEOMETRY ?? '')
    .trim()
    .toLowerCase() === 'true';

  const runKeyVaultConnectivityCheck = async () => {
    setKvLoading(true);
    setKvError(null);
    setKvResult(null);
    setCopyDone(false);
    try {
      const data = await debugService.checkKeyVaultConnectivity();
      setKvResult(data);
    } catch (err) {
      setKvError(err.message || 'Key Vault check failed');
    } finally {
      setKvLoading(false);
    }
  };

  const copyKvResultToClipboard = async () => {
    if (!kvResult) return;
    const text = JSON.stringify(kvResult, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    } catch (err) {
      setKvError(err.message || 'Could not copy to clipboard');
    }
  };

  const runCanadaHolidaysCheck = async () => {
    setHolidayLoading(true);
    setHolidayError(null);
    setHolidayResult(null);
    setHolidayCopyDone(false);
    try {
      const y = Number.parseInt(holidayYear, 10);
      const data = await debugService.probeCanadianPublicHolidays(
        Number.isFinite(y) ? y : new Date().getFullYear(),
        holProvince
      );
      setHolidayResult(data);
    } catch (err) {
      setHolidayError(err.message || 'Canada holidays probe failed');
    } finally {
      setHolidayLoading(false);
    }
  };

  const copyHolidayResultToClipboard = async () => {
    if (!holidayResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(holidayResult, null, 2));
      setHolidayCopyDone(true);
      setTimeout(() => setHolidayCopyDone(false), 2000);
    } catch (err) {
      setHolidayError(err.message || 'Could not copy to clipboard');
    }
  };

  const runDrillzConnectivityCheck = async () => {
    setDrillzLoading(true);
    setDrillzError(null);
    setDrillzResult(null);
    setDrillzCopyDone(false);
    try {
      const data = await debugService.checkDrillzTenantConnection();
      setDrillzResult(data);
    } catch (err) {
      setDrillzError(err.message || 'Drillz connection check failed');
    } finally {
      setDrillzLoading(false);
    }
  };

  const copyDrillzResultToClipboard = async () => {
    if (!drillzResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(drillzResult, null, 2));
      setDrillzCopyDone(true);
      setTimeout(() => setDrillzCopyDone(false), 2000);
    } catch (err) {
      setDrillzError(err.message || 'Could not copy to clipboard');
    }
  };

  const runListValuesDebugCheck = async () => {
    setListValuesLoading(true);
    setListValuesError(null);
    setListValuesResult(null);
    setListValuesCopyDone(false);
    try {
      const data = await debugService.checkDrillzListValues(
        listValuesTableCode,
        listValuesFieldCodes
      );
      setListValuesResult(data);
    } catch (err) {
      setListValuesError(err.message || 'List values debug check failed');
    } finally {
      setListValuesLoading(false);
    }
  };

  const copyListValuesResultToClipboard = async () => {
    if (!listValuesResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(listValuesResult, null, 2));
      setListValuesCopyDone(true);
      setTimeout(() => setListValuesCopyDone(false), 2000);
    } catch (err) {
      setListValuesError(err.message || 'Could not copy to clipboard');
    }
  };

  const runGeometryDebugCheck = async () => {
    setGeomLoading(true);
    setGeomError(null);
    setGeomResult(null);
    setGeomCopyDone(false);
    try {
      const data = await debugService.checkDrillzGeometry(geomHoleId);
      setGeomResult(data);
    } catch (err) {
      setGeomError(err.message || 'Geometry debug check failed');
    } finally {
      setGeomLoading(false);
    }
  };

  const copyGeomResultToClipboard = async () => {
    if (!geomResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(geomResult, null, 2));
      setGeomCopyDone(true);
      setTimeout(() => setGeomCopyDone(false), 2000);
    } catch (err) {
      setGeomError(err.message || 'Could not copy to clipboard');
    }
  };

  const runProcurifyConnectivity = async () => {
    setProcurifyLoading(true);
    setProcurifyError(null);
    setProcurifyResult(null);
    setProcurifyCopyDone(false);
    try {
      const data = await debugService.fetchProcurifyBills({ mode: 'connectivity' });
      setProcurifyResult(data);
      if (!data.ok) {
        setProcurifyError(
          data.body?.message || `Procurify connectivity failed (${data.status}).`
        );
      }
    } catch (err) {
      setProcurifyError(err.message || 'Procurify connectivity check failed');
    } finally {
      setProcurifyLoading(false);
    }
  };

  const runProcurifyFetchBills = async () => {
    setProcurifyLoading(true);
    setProcurifyError(null);
    setProcurifyResult(null);
    setProcurifyCopyDone(false);
    try {
      const page = Number.parseInt(procurifyPage, 10);
      const pageSize = Number.parseInt(procurifyPageSize, 10);
      const data = await debugService.fetchProcurifyBills({
        mode: 'bills',
        assigneeEmail: procurifyAssigneeEmail,
        page: Number.isFinite(page) ? page : 1,
        pageSize: Number.isFinite(pageSize) ? pageSize : 25,
        includeRaw: procurifyIncludeRaw,
      });
      setProcurifyResult(data);
      if (!data.ok || data.body?.success === false) {
        setProcurifyError(
          data.body?.message ||
            data.body?.error ||
            `Procurify bills request failed (${data.status}).`
        );
      }
    } catch (err) {
      setProcurifyError(err.message || 'Procurify bills fetch failed');
    } finally {
      setProcurifyLoading(false);
    }
  };

  const copyProcurifyResultToClipboard = async () => {
    if (!procurifyResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(procurifyResult, null, 2));
      setProcurifyCopyDone(true);
      setTimeout(() => setProcurifyCopyDone(false), 2000);
    } catch (err) {
      setProcurifyError(err.message || 'Could not copy to clipboard');
    }
  };

  return (
    <Layout user={user} currentRoute="/debug">
      <div className="debug">
        <div className="debug-header">
          <IconText icon={ICON.appDebug} as="h1">Debug Dashboard</IconText>
          <p>Debug tools and diagnostics</p>
        </div>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Key Vault connectivity</h2>
          <p className="kv-check-desc">
            Calls <code className="kv-check-code">GET /api/kv_connectivity_check</code> with your
            session token. Verifies that <code className="kv-check-code">KV_CONNECTIVITY_TEST</code>{' '}
            resolves from Key Vault (managed identity + firewall).
          </p>
          <div className="kv-check-toolbar">
            <Button
              type="button"
              onClick={runKeyVaultConnectivityCheck}
              loading={kvLoading}
              variant="primary"
            >
              Run Key Vault check
            </Button>
            <Button
              type="button"
              onClick={copyKvResultToClipboard}
              disabled={!kvResult || kvLoading}
              variant="secondary"
            >
              {copyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {kvError && (
            <p className="kv-check-error" role="alert">
              {kvError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="kv-check-result">
              Result
            </label>
            <textarea
              id="kv-check-result"
              className="kv-check-result"
              readOnly
              rows={12}
              placeholder="Click “Run Key Vault check” to see HTTP status and JSON from the Function App."
              value={
                kvResult
                  ? JSON.stringify(kvResult, null, 2)
                  : ''
              }
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Procurify bills (assigned approver)</h2>
          <p className="kv-check-desc">
            Calls{' '}
            <code className="kv-check-code">GET /api/procurify_bills_debug</code> with your
            session token (requires <code className="kv-check-code">wlfwkr-debug</code>). OAuth
            credentials live in the Function App only — see{' '}
            <code className="kv-check-code">PROCURIFY_*</code> settings. Lists AP bills for the
            assignee email via Procurify{' '}
            <a
              className="kv-check-link"
              href="https://developer.procurify.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              API
            </a>{' '}
            (
            <a
              className="kv-check-link"
              href="https://selkirkmining.procurify.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              selkirkmining.procurify.com
            </a>
            ).
          </p>
          <div className="kv-check-toolbar kv-check-toolbar--holiday-fields">
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Assignee email</span>
              <input
                type="email"
                className="kv-check-input"
                value={procurifyAssigneeEmail}
                onChange={(e) => setProcurifyAssigneeEmail(e.target.value)}
                placeholder="riyan.abraham@selkirkcopper.com"
              />
            </label>
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Page</span>
              <input
                type="number"
                className="kv-check-input kv-check-input-year"
                min={1}
                value={procurifyPage}
                onChange={(e) => setProcurifyPage(e.target.value)}
              />
            </label>
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Page size</span>
              <input
                type="number"
                className="kv-check-input kv-check-input-year"
                min={1}
                max={100}
                value={procurifyPageSize}
                onChange={(e) => setProcurifyPageSize(e.target.value)}
              />
            </label>
            <label className="kv-check-inline-field kv-check-inline-field--checkbox">
              <input
                type="checkbox"
                checked={procurifyIncludeRaw}
                onChange={(e) => setProcurifyIncludeRaw(e.target.checked)}
              />
              <span className="kv-check-inline-label">Include raw Procurify JSON</span>
            </label>
            <Button
              type="button"
              onClick={runProcurifyConnectivity}
              loading={procurifyLoading}
              variant="secondary"
            >
              Run connectivity check
            </Button>
            <Button
              type="button"
              onClick={runProcurifyFetchBills}
              loading={procurifyLoading}
              variant="primary"
            >
              Fetch bills
            </Button>
            <Button
              type="button"
              onClick={copyProcurifyResultToClipboard}
              disabled={!procurifyResult || procurifyLoading}
              variant="secondary"
            >
              {procurifyCopyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {procurifyError && (
            <p className="kv-check-error" role="alert">
              {procurifyError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="procurify-debug-result">
              Result
            </label>
            <textarea
              id="procurify-debug-result"
              className="kv-check-result"
              readOnly
              rows={16}
              placeholder="Run connectivity after setting PROCURIFY_DOMAIN, PROCURIFY_CLIENT_ID, and PROCURIFY_CLIENT_SECRET on the Function App, then fetch bills."
              value={
                procurifyResult
                  ? JSON.stringify(procurifyResult, null, 2)
                  : ''
              }
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Drillz tenant database</h2>
          <p className="kv-check-desc">
            Calls <code className="kv-check-code">GET /api/drillz_connectivity_check</code> with your
            session token. Uses <code className="kv-check-code">DRILLZ_TENANT_CONN_STR</code> (Key Vault)
            and runs{' '}
            <code className="kv-check-code">SELECT * FROM tenant_data.v_drillz_campaign_progress LIMIT 25</code>.
          </p>
          <div className="kv-check-toolbar">
            <Button
              type="button"
              onClick={runDrillzConnectivityCheck}
              loading={drillzLoading}
              variant="primary"
            >
              Run Drillz check
            </Button>
            <Button
              type="button"
              onClick={copyDrillzResultToClipboard}
              disabled={!drillzResult || drillzLoading}
              variant="secondary"
            >
              {drillzCopyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {drillzError && (
            <p className="kv-check-error" role="alert">
              {drillzError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="drillz-debug-result">
              Result
            </label>
            <textarea
              id="drillz-debug-result"
              className="kv-check-result"
              readOnly
              rows={14}
              placeholder="Run check to see connection status and sample rows from v_drillz_campaign_progress."
              value={
                drillzResult
                  ? JSON.stringify(drillzResult, null, 2)
                  : ''
              }
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Quicklog list visuals (tenant_app lists)</h2>
          <p className="kv-check-desc">
            Calls{' '}
            <code className="kv-check-code">GET /api/drillz_list_values_debug</code>
            {' '}
            with <code className="kv-check-code">table_code</code> and{' '}
            <code className="kv-check-code">field_codes</code> to resolve{' '}
            <code className="kv-check-code">field_definitions.list_id</code> for the quicklog table.
            Compare <code className="kv-check-code">quicklogFieldBindings</code> to legacy{' '}
            <code className="kv-check-code">requestedListsQuery</code> (list_code name filter).
          </p>
          <div className="kv-check-toolbar kv-check-toolbar--holiday-fields">
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">table_code</span>
              <input
                type="text"
                className="kv-check-input"
                value={listValuesTableCode}
                onChange={(e) => setListValuesTableCode(e.target.value)}
                placeholder="quicklog"
              />
            </label>
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">field_codes</span>
              <input
                type="text"
                className="kv-check-input"
                value={listValuesFieldCodes}
                onChange={(e) => setListValuesFieldCodes(e.target.value)}
                placeholder="rock_type,min_style,min_type"
              />
            </label>
            <Button
              type="button"
              onClick={runListValuesDebugCheck}
              loading={listValuesLoading}
              variant="primary"
            >
              Run list values check
            </Button>
            <Button
              type="button"
              onClick={copyListValuesResultToClipboard}
              disabled={!listValuesResult || listValuesLoading}
              variant="secondary"
            >
              {listValuesCopyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {listValuesError && (
            <p className="kv-check-error" role="alert">
              {listValuesError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="list-values-debug-result">
              Result
            </label>
            <textarea
              id="list-values-debug-result"
              className="kv-check-result"
              readOnly
              rows={16}
              placeholder="Run check to see field→list_id bindings and value counts per quicklog field."
              value={
                listValuesResult
                  ? JSON.stringify(listValuesResult, null, 2)
                  : ''
              }
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">3D geometry (Drillz tenant_geometry)</h2>
          <p className="kv-check-desc">
            Build flag <code className="kv-check-code">REACT_APP_3D_USE_DRILLZ_GEOMETRY</code>
            {' '}
            is
            {' '}
            <strong>{drillzGeometryFlagOn ? 'ON' : 'OFF'}</strong>
            {' '}
            in this bundle (requires dev-server restart or redeploy after changing .env;
            production SWA bakes this at <code className="kv-check-code">npm run build</code> in CI).
            Calls
            {' '}
            <code className="kv-check-code">GET /api/drillz_geometry_debug?hole_id=…</code>
            {' '}
            to inspect collar, trace status, row_geometry counts, and scene preview.
          </p>
          <div className="kv-check-toolbar kv-check-toolbar--holiday-fields">
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Hole ID</span>
              <input
                type="text"
                className="kv-check-input"
                value={geomHoleId}
                onChange={(e) => setGeomHoleId(e.target.value)}
                placeholder="26SCM358"
              />
            </label>
            <Button
              type="button"
              onClick={runGeometryDebugCheck}
              loading={geomLoading}
              variant="primary"
            >
              Run geometry check
            </Button>
            <Button
              type="button"
              onClick={copyGeomResultToClipboard}
              disabled={!geomResult || geomLoading}
              variant="secondary"
            >
              {geomCopyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {geomError && (
            <p className="kv-check-error" role="alert">
              {geomError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="geom-debug-result">
              Result
            </label>
            <textarea
              id="geom-debug-result"
              className="kv-check-result"
              readOnly
              rows={16}
              placeholder="Run check for a hole that renders in Drillz but not Wolfwalker 3D."
              value={geomResult ? JSON.stringify(geomResult, null, 2) : ''}
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Canada public holidays (Who’s In source)</h2>
          <p className="kv-check-desc">
            Uses the same public API as{' '}
            <code className="kv-check-code">src/utils/whosIn/canadaHolidays.js</code> —
            <code className="kv-check-code">{` GET https://canada-holidays.ca/api/v1/holidays`}</code>
            (with optional <code className="kv-check-code">?year=…</code> then fallback).
            Parses <code className="kv-check-code">holidays</code> filtered by province, matching Who&apos;s In.
          </p>
          <div className="kv-check-toolbar kv-check-toolbar--holiday-fields">
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Year</span>
              <input
                type="number"
                className="kv-check-input kv-check-input-year"
                min={1900}
                max={2200}
                value={holidayYear}
                onChange={(e) => setHolidayYear(e.target.value)}
              />
            </label>
            <label className="kv-check-inline-field">
              <span className="kv-check-inline-label">Province</span>
              <select
                className="kv-check-input kv-check-select-province"
                value={holProvince}
                onChange={(e) => setHolProvince(e.target.value)}
                aria-label="Province code"
              >
                <option value="BC">BC</option>
                <option value="AB">AB</option>
                <option value="SK">SK</option>
                <option value="MB">MB</option>
                <option value="ON">ON</option>
                <option value="QC">QC</option>
                <option value="NB">NB</option>
                <option value="NS">NS</option>
                <option value="PE">PE</option>
                <option value="NL">NL</option>
                <option value="YT">YT</option>
                <option value="NT">NT</option>
                <option value="NU">NU</option>
              </select>
            </label>
            <Button
              type="button"
              onClick={runCanadaHolidaysCheck}
              loading={holidayLoading}
              variant="primary"
            >
              Run holidays probe
            </Button>
            <Button
              type="button"
              onClick={copyHolidayResultToClipboard}
              disabled={!holidayResult || holidayLoading}
              variant="secondary"
            >
              {holidayCopyDone ? 'Copied' : 'Copy result'}
            </Button>
          </div>
          {holidayError && (
            <p className="kv-check-error" role="alert">
              {holidayError}
            </p>
          )}
          <div className="kv-check-result-wrap">
            <label className="kv-check-label" htmlFor="holiday-debug-result">
              Result
            </label>
            <textarea
              id="holiday-debug-result"
              className="kv-check-result"
              readOnly
              rows={14}
              placeholder="Run probe to see HTTP steps, truncated bodies, and province-filtered holidays for the year."
              value={
                holidayResult
                  ? JSON.stringify(holidayResult, null, 2)
                  : ''
              }
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default Debug;
